import type { PaletteColorName } from '@/features/colors/color.types';
import {
  getColorPalette,
  toPaletteColorName,
} from '@/features/colors/lib/paletteLookup';
import {
  DEFAULT_EXPERIENCE_VIBE,
  getVibe,
  toExperienceVibe,
} from '@/features/colors/lib/vibeLookup';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { Box } from '@/ui';
import { SidebarSection } from '../SidebarKit';
import { useCustomizeControl } from '../useCustomizeControl';
import { AccentColorGrid } from './AccentColorGrid';
import { BackgroundSection } from './BackgroundSection';
import { CardStyleSection } from './CardStyleSection';
import { PaletteSelect } from './PaletteSelect';
import { VibeSelect } from './VibeSelect';

export const StylePanel = () => {
  const { persistedExperience } = usePageExperienceContext();
  const { register } = useCustomizeControl();

  // Read from the saved schedule, never the previewed one, so no control here
  // looks like it has already applied what is only being previewed on the page.
  const activePalette = getColorPalette(persistedExperience.colorPalette);
  // Resolved from the saved vibe rather than useVibe(), which reads the
  // previewed one: hovering a vibe that drops accent support would otherwise
  // take the whole section out from under the pointer mid-hover.
  const savedVibe = getVibe(persistedExperience.vibe);

  return (
    <Box flex={1} overflowY="auto" p={4} pb={10}>
      <SidebarSection label="Vibe" scope="For all views">
        <VibeSelect
          {...register('experience', 'vibe', {
            // The column is nullable; an experience that has never had a vibe
            // chosen shows the default rather than an empty trigger.
            toControl: (vibe) =>
              toExperienceVibe(vibe) ?? DEFAULT_EXPERIENCE_VIBE,
          })}
        />
      </SidebarSection>

      <SidebarSection label="Color palette" scope="For all views">
        <PaletteSelect {...register('experience', 'colorPalette')} />
      </SidebarSection>

      {/* Both hidden on a vibe that never reads primaryColor (Planner), where every
          swatch — and the background those swatches tint — would be a control with no
          visible effect. */}
      {savedVibe.supportedDisplaySettings.accentColor && (
        <>
          <SidebarSection label="Accent color" scope="For all views">
            <AccentColorGrid
              palette={activePalette}
              {...register<'primaryColor', PaletteColorName>(
                'experience',
                'primaryColor',
                // The column is free-form, so coerce rather than assert: a retired or
                // unrecognised value resolves to the default slot the way every other
                // palette read does. Asserting instead left the grid with no swatch
                // selected, since the stored value matched none of them.
                { toControl: toPaletteColorName }
              )}
            />
          </SidebarSection>

          <BackgroundSection
            colorName={persistedExperience.primaryColor}
            isEnabled={persistedExperience.accentBackgroundEnabled}
            palette={activePalette}
          />
        </>
      )}

      {/* Outside the accent gate: the samples are colored by the palette, not the
          accent, so the Planner vibe — which offers no accent at all — still needs
          the control. */}
      <CardStyleSection />
    </Box>
  );
};
